import React, {useMemo} from 'react';
import {Grid, GridItem, Badge, Box} from '@chakra-ui/react';
import {DataGrid} from '../../components/data-grid/data-grid';
import {
  CellClassParams,
  CellStyle,
  ColDef,
  GridOptions,
  ICellRendererParams,
  RowClickedEvent,
  ValueGetterParams
} from '@codametrix/grid';
import {SubHeader} from '../organizations/subheader';
import {ORGANIZATION_CONSTANTS} from '../organizations/organization-constants';
import {styles} from './configuration.styles';
import {useHistory} from 'react-router-dom';
import {configurationUtils} from '@codametrix/ui-common';

const { getConfigValueForServiceLine } = configurationUtils;

interface ConfigurationsDataGridProps {
  configurations: CMxAPI.Configuration[];
  serviceLines?: CMx.ServiceLine[];
  pageSize?: number;
  orgCode?: string;
  tenantId: string;
}

const FieldKeynameCellRenderer: React.FC<ICellRendererParams> = params => {
  const rowData = params.data as CMxAPI.Configuration;
  const isSystemLevel = params.context?.isSystemLevel || false;

  return (
      <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            padding: '4px 0'
          }}>
        <Box display="flex" alignItems="center" gap={2}>
          <div>
            <div style={{ fontWeight: 'bold' }}>{rowData.keyname}</div>
            {rowData.displayName && (
                <div style={{ fontSize: '0.85em', color: 'gray' }}>
                  {rowData.displayName}
                </div>
            )}
          </div>
          {isSystemLevel && (
              <Badge colorScheme="purple" size="sm">
                System
              </Badge>
          )}
        </Box>
      </div>
  );
};

// Helper function to format values for table display (simplified for JSON)
const formatTableDisplayValue = (
    value: string | string[] | object | null | undefined
): JSX.Element | string => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return <span style={{ color: 'gray' }}>null</span>;
  }

  // For objects (JSON), show a simple indicator instead of full content
  if (typeof value === 'object' && !Array.isArray(value)) {
    return (
        <span
            style={{
              color: '#0066cc',
              fontStyle: 'italic',
              fontSize: '0.9em',
              backgroundColor: '#f0f8ff',
              padding: '2px 6px',
              borderRadius: '3px',
              border: '1px solid #e0e0e0'
            }}>
        JSON Value
      </span>
    );
  }

  if (Array.isArray(value)) {
    if (value.length > 1) {
      return `[${value.join(', ')}]`;
    }
    return value[0];
  }

  return value;
};

const ConfigurationsTable: React.FC<ConfigurationsDataGridProps> = ({
                                                                      configurations,
                                                                      serviceLines,
                                                                      pageSize = 100,
                                                                      orgCode,
                                                                      tenantId
                                                                    }) => {
  const classes = styles();
  const history = useHistory();
  const isSystemLevel = tenantId === '*';

  const handleRowClicked = (event: RowClickedEvent) => {
    const config = event.data as CMxAPI.Configuration;
    history.push(`/cmx/admin/orgs/${orgCode}/configurations/${config.id}`);
  };

  const colDefs: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        headerName: 'Field (Keyname)',
        field: 'keyname',
        cellRendererFramework: FieldKeynameCellRenderer,
        flex: 1,
        minWidth: 150,
        autoHeight: true,
        wrapText: true,
        sort: 'asc'
      },
      {
        headerName: 'Section',
        field: 'section',
        valueGetter: (params: ValueGetterParams) => {
          const rowData = params.data as CMxAPI.Configuration;
          return rowData.section ?? '';
        },
        cellRenderer: (params: ICellRendererParams) => (
            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {params.value || '—'}
            </div>
        ),
        flex: 1,
        minWidth: 100,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          'white-space': 'normal',
          'word-break': 'break-word'
        }
      },
      {
        headerName: isSystemLevel ? 'System Default' : 'Tenant Default',
        valueGetter: (params: ValueGetterParams) => {
          const rowData = params.data as CMxAPI.Configuration;
          return getConfigValueForServiceLine(rowData.values, '*', tenantId);
        },
        cellRenderer: (params: ICellRendererParams) => {
          const formattedValue = formatTableDisplayValue(params.value);
          return (
              <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {formattedValue}
              </div>
          );
        },
        flex: 1,
        minWidth: 120,
        wrapText: true,
        autoHeight: true,
        cellStyle: (params: CellClassParams): CellStyle => {
          const style: CellStyle = {
            'white-space': 'normal',
            'word-break': 'break-word',
            color: 'inherit',
          };
          if (
              !params.value ||
              (Array.isArray(params.value) && params.value.length === 0)
          ) {
            style.color = 'lightgrey';
          }
          return style;
        }
      }
    ];

    // Always show service line columns if serviceLines are available
    if (serviceLines && serviceLines.length > 0) {
      serviceLines.forEach(sl => {
        columns.push({
          headerName: sl.name,
          valueGetter: (params: ValueGetterParams) => {
            const rowData = params.data as CMxAPI.Configuration;
            return getConfigValueForServiceLine(
                rowData.values,
                sl.name,
                tenantId
            );
          },
          cellRenderer: (params: ICellRendererParams) => {
            const formattedValue = formatTableDisplayValue(params.value);
            return (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {formattedValue}
                </div>
            );
          },
          flex: 1,
          minWidth: 100,
          wrapText: true,
          autoHeight: true,
          cellStyle: (params: CellClassParams): CellStyle => {
            const style: CellStyle = {
              'white-space': 'normal',
              'word-break': 'break-word',
              color: 'inherit'
            };
            if (
                !params.value ||
                (Array.isArray(params.value) && params.value.length === 0)
            ) {
              style.color = 'lightgrey';
            }
            return style;
          }
        });
      });
    }

    return columns;
  }, [serviceLines, tenantId, isSystemLevel]);

  const gridOptions: GridOptions = {
    suppressHorizontalScroll: true,
    onRowClicked: handleRowClicked,
    context: {
      isSystemLevel
    }
  };

  const tableTitle = isSystemLevel
      ? `${ORGANIZATION_CONSTANTS.CONFIGURATIONS} (System Level)`
      : ORGANIZATION_CONSTANTS.CONFIGURATIONS;

  return (
      <Grid display="flex" flexDirection="column" rowGap={5}>
        <GridItem>
          <SubHeader
              className={classes.text}
              title={tableTitle}
          />
        </GridItem>
        <DataGrid
            rowData={configurations}
            columnDefs={colDefs}
            gridOptions={gridOptions}
            paginationPageSize={pageSize}
        />
      </Grid>
  );
};

export default ConfigurationsTable;

